// TelcoTrack — aktuální KPI karty (stejná data jako manager-kpi-chart, bez časové osy).

import {
  MANAGER_KPI_CARD_INFO,
  MANAGER_KPI_CARD_LABELS,
  MANAGER_KPI_CATEGORY_CARDS,
  MANAGER_KPI_CATEGORY_HEADLINE,
  MANAGER_KPI_CATEGORY_IDS,
  MANAGER_KPI_CATEGORY_INFO,
  MANAGER_KPI_CATEGORY_LABELS,
  MANAGER_KPI_CATEGORY_SUMMARIES,
  MANAGER_KPI_HEADLINE_INFO,
  isManagerKpiAbsoluteCardId,
  isManagerKpiBuildingDualCardId,
  isManagerKpiCategoryDrillable,
  managerKpiCategoryDetailCards,
  type ManagerKpiCardId,
  type ManagerKpiCategoryId,
} from '@core/shared-bridge';
import type {
  ManagerDashboardMetrics,
  ManagerDashboardTimeseriesResult,
} from '../../core/models/object.model';
import { MANAGER_KPI_SERIES_COLORS } from './manager-kpi-chart.echarts';

/** Změna oproti minulému týdnu: plus zeleně, minus červeně, 0 šedě. */
export type ManagerKpiDeltaTone = 'up' | 'down' | 'flat';

/** Jedna strana zlomku minulého týdne (absolutní + delta). */
export interface ManagerKpiPriorWeekSideView {
  absoluteLabel: string;
  deltaLabel: string;
  deltaTone: ManagerKpiDeltaTone;
}

/** Absolutní hodnoty KPI z minulého týdne (pod čitatelem / jmenovatelem) + delta. */
export interface ManagerKpiPriorWeekPairView {
  numerator: ManagerKpiPriorWeekSideView;
  denominator: ManagerKpiPriorWeekSideView;
}

/** Sekundární zlomek na kartě (HP u statistik budov) — pie zůstává na primary. */
export interface ManagerKpiSecondaryPairView {
  numeratorLabel: string;
  denominatorLabel: string;
  unitSuffix: string;
  valueLabel: string;
  numeratorValue: number | null;
  denominatorValue: number | null;
  priorWeek: ManagerKpiPriorWeekPairView | null;
}

export interface ManagerKpiSnapshotCardView {
  id: string;
  label: string;
  /** České vysvětlení zlomek/absolutní hodnoty (info tlačítko). */
  info: string;
  /** Barva řady z KPI grafu (levé okraje karty). */
  seriesColor: string;
  /**
   * `fraction` = hotovo/celkem (`n / m`); `absolute` = jedno číslo (např. zafoukáno m).
   */
  valueMode: 'fraction' | 'absolute';
  /** Jednořádkový souhrn (a11y / testy), např. `10 000 / 50 000 m` nebo `12 / 40 ks · 80 / 200 HP`. */
  valueLabel: string;
  numeratorLabel: string;
  denominatorLabel: string;
  /** Přípona jednotky (` m` / ` ks` / ` HP`). */
  unitSuffix: string;
  /**
   * Číselný čitatel / jmenovatel pro kompaktní pie (null = žádná data / absolute bez pie).
   * U dual ks·HP = vždy ks (primary).
   */
  numeratorValue: number | null;
  denominatorValue: number | null;
  /** Absolutní stav před ~7 dny + delta; `null` pokud timeseries nemá blízký bucket. */
  priorWeek: ManagerKpiPriorWeekPairView | null;
  /**
   * Druhý zlomek (HP) u building dual karet; `null` = jen primary.
   * Pie / primary řádky zůstávají na ks.
   */
  secondaryPair: ManagerKpiSecondaryPairView | null;
  /**
   * Volitelný textový řádek pod hodnotou (ne zlomek) — detail karty, ne hub dlaždice.
   */
  secondaryText?: string | null;
  /** Šedý podtitul hub dlaždice (extra karty). Detail ho nepoužívá. */
  summary?: string | null;
}

/** Pie na kartě: fraction + kladný jmenovatel. */
export function managerKpiCardShowsPie(card: ManagerKpiSnapshotCardView): boolean {
  return (
    card.valueMode === 'fraction' &&
    card.denominatorValue != null &&
    Number.isFinite(card.denominatorValue) &&
    card.denominatorValue > 0 &&
    card.numeratorValue != null &&
    Number.isFinite(card.numeratorValue)
  );
}

export type ManagerKpiSnapshotRowId = ManagerKpiCategoryId;

/** Nadpisy řad vlevo od snapshot karet — SSOT `@cubby/shared` manager-kpi-categories. */
export const MANAGER_KPI_ROW_TITLES = MANAGER_KPI_CATEGORY_LABELS;

/**
 * `portfolio` — hotovo / celkem (dashboard, polygon).
 * `userPeriodWork` — jen odvedená práce uživatele v kalendářním období (bez jmenovatelů).
 */
export type ManagerKpiPresentationMode = 'portfolio' | 'userPeriodWork';

/** Jedna řada snapshot karet s nadpisem vlevo. */
export interface ManagerKpiSnapshotRowView {
  id: ManagerKpiSnapshotRowId;
  title: string;
  cards: ManagerKpiSnapshotCardView[];
}

/** Hub dlaždice kategorie (board) — klik → detail (`drillable` vždy true). */
export interface ManagerKpiCategoryTileView {
  id: ManagerKpiCategoryId;
  title: string;
  summary: string;
  /** České vysvětlení hub kategorie (info „i“). */
  info: string;
  seriesColor: string;
  headline: ManagerKpiSnapshotCardView;
  /** `false` = jedna karta → bez chevronu / navigace. */
  drillable: boolean;
}

/** Rozpad OK podle typu / počtu vláken (detail Záfuk) — zafoukáno jako absolutní m. */
export interface ManagerKpiOkTypBreakdownView {
  typOk: string;
  fiberLabel: string;
  /** Zafoukáno (completedLengthM), formátované. */
  numeratorLabel: string;
  unitSuffix: string;
  valueLabel: string;
}

function fmtInt(v: number): string {
  return `${Math.round(Number(v) || 0).toLocaleString('cs-CZ')}`;
}

function fmtMeters(v: number): string {
  return Number(v).toLocaleString('cs-CZ', { maximumFractionDigits: Math.abs(v) >= 1000 ? 0 : 1 });
}

type KpiPairValue = { numerator: number; denominator: number; unit: 'm' | 'ks' | 'hp' };

function formatPairLabels(
  pair: KpiPairValue,
): Pick<
  ManagerKpiSnapshotCardView,
  | 'valueMode'
  | 'numeratorLabel'
  | 'denominatorLabel'
  | 'unitSuffix'
  | 'valueLabel'
  | 'numeratorValue'
  | 'denominatorValue'
> {
  if (pair.unit === 'hp') {
    const numeratorLabel = fmtInt(pair.numerator);
    const denominatorLabel = fmtInt(pair.denominator);
    return {
      valueMode: 'fraction',
      numeratorLabel,
      denominatorLabel,
      unitSuffix: ' HP',
      valueLabel: `${numeratorLabel} / ${denominatorLabel} HP`,
      numeratorValue: pair.numerator,
      denominatorValue: pair.denominator,
    };
  }
  if (pair.unit === 'm') {
    const numeratorLabel = fmtMeters(pair.numerator);
    const denominatorLabel = fmtMeters(pair.denominator);
    return {
      valueMode: 'fraction',
      numeratorLabel,
      denominatorLabel,
      unitSuffix: ' m',
      valueLabel: `${numeratorLabel} / ${denominatorLabel} m`,
      numeratorValue: pair.numerator,
      denominatorValue: pair.denominator,
    };
  }
  const numeratorLabel = fmtInt(pair.numerator);
  const denominatorLabel = fmtInt(pair.denominator);
  return {
    valueMode: 'fraction',
    numeratorLabel,
    denominatorLabel,
    unitSuffix: ' ks',
    valueLabel: `${numeratorLabel} / ${denominatorLabel} ks`,
    numeratorValue: pair.numerator,
    denominatorValue: pair.denominator,
  };
}

/** Jedno absolutní číslo (např. zafoukáno m) — bez zlomku n/m. */
function formatAbsoluteLabels(
  pair: KpiPairValue,
): Pick<
  ManagerKpiSnapshotCardView,
  | 'valueMode'
  | 'numeratorLabel'
  | 'denominatorLabel'
  | 'unitSuffix'
  | 'valueLabel'
  | 'numeratorValue'
  | 'denominatorValue'
> {
  if (pair.unit === 'm') {
    const numeratorLabel = fmtMeters(pair.numerator);
    return {
      valueMode: 'absolute',
      numeratorLabel,
      denominatorLabel: '',
      unitSuffix: ' m',
      valueLabel: `${numeratorLabel} m`,
      numeratorValue: pair.numerator,
      denominatorValue: null,
    };
  }
  if (pair.unit === 'hp') {
    const numeratorLabel = fmtInt(pair.numerator);
    return {
      valueMode: 'absolute',
      numeratorLabel,
      denominatorLabel: '',
      unitSuffix: ' HP',
      valueLabel: `${numeratorLabel} HP`,
      numeratorValue: pair.numerator,
      denominatorValue: null,
    };
  }
  const numeratorLabel = fmtInt(pair.numerator);
  return {
    valueMode: 'absolute',
    numeratorLabel,
    denominatorLabel: '',
    unitSuffix: ' ks',
    valueLabel: `${numeratorLabel} ks`,
    numeratorValue: pair.numerator,
    denominatorValue: null,
  };
}

function formatPriorWeekAbsolute(
  current: KpiPairValue,
  previous: KpiPairValue,
): ManagerKpiPriorWeekPairView {
  const unit = current.unit;
  const numerator = {
    absoluteLabel: formatAbsoluteSide(previous.numerator, unit),
    ...formatKpiDelta(current.numerator, previous.numerator, unit),
  };
  // Absolute karty nemají jmenovatel — denomiátor držíme flat kvůli typu.
  return {
    numerator,
    denominator: { absoluteLabel: '', deltaLabel: '0', deltaTone: 'flat' },
  };
}

/**
 * Prior-week absolute — jen číslo (bez jednotky).
 * Jednotka už je u aktuální hodnoty (`3 / 7 ks` / `1 250 m`); opakovat `3 ks` vedle `3` dělalo zmatek
 * (např. Postavené rozvaděče: `3`, `3 ks`, `0` / `7 ks`).
 */
function formatAbsoluteSide(value: number, unit: 'm' | 'ks' | 'hp'): string {
  if (unit === 'm') return fmtMeters(value);
  return fmtInt(value);
}

/** Delta = current − previous (nárůst = +, pokles = −). */
export function formatKpiDelta(
  current: number,
  previous: number,
  unit: 'm' | 'ks' | 'hp',
): Pick<ManagerKpiPriorWeekSideView, 'deltaLabel' | 'deltaTone'> {
  const raw = (Number(current) || 0) - (Number(previous) || 0);
  const rounded =
    unit === 'm' ? Number(raw.toFixed(Math.abs(raw) >= 1000 ? 0 : 1)) : Math.round(raw);
  if (rounded === 0) {
    return { deltaLabel: '0', deltaTone: 'flat' };
  }
  const absLabel = unit === 'm' ? fmtMeters(Math.abs(rounded)) : fmtInt(Math.abs(rounded));
  if (rounded > 0) {
    return { deltaLabel: `+${absLabel}`, deltaTone: 'up' };
  }
  return { deltaLabel: `-${absLabel}`, deltaTone: 'down' };
}

function formatPriorWeekPair(current: KpiPairValue, previous: KpiPairValue): ManagerKpiPriorWeekPairView {
  const unit = current.unit;
  return {
    numerator: {
      absoluteLabel: formatAbsoluteSide(previous.numerator, unit),
      ...formatKpiDelta(current.numerator, previous.numerator, unit),
    },
    denominator: {
      absoluteLabel: formatAbsoluteSide(previous.denominator, unit),
      ...formatKpiDelta(current.denominator, previous.denominator, unit),
    },
  };
}

/** Viditelné karty + interní hub součty (`routes`, `zafukBudovy`). */
export type { ManagerKpiCardId };
type ManagerKpiValueId = ManagerKpiCardId | 'routes' | 'zafukBudovy';

type KpiPrimaryValue = { amount: number; unit: 'm' | 'ks' | 'hp' };

/** Součet záfuku připojovaných budov + bábovek (ks) — hub headline Záfuk. */
function sumZafukBudovyKsPair(metrics: ManagerDashboardMetrics): KpiPairValue {
  return {
    numerator:
      Math.max(0, metrics.zafoukPripojenaBudova?.finishedCount ?? 0) +
      Math.max(0, metrics.zafoukBabovka?.finishedCount ?? 0),
    denominator:
      Math.max(0, metrics.zafoukPripojenaBudova?.totalCount ?? 0) +
      Math.max(0, metrics.zafoukBabovka?.totalCount ?? 0),
    unit: 'ks',
  };
}

/**
 * Součet HP ve stejném scope jako `zafukBudovy` ks.
 * `null` jen když ani jedna ze split metrik HP ještě neposílá (starý cache payload).
 */
function sumZafukBudovyHpPair(metrics: ManagerDashboardMetrics): KpiPairValue | null {
  const pripojena = metrics.zafoukPripojenaBudova;
  const babovka = metrics.zafoukBabovka;
  const hasHp =
    pripojena?.finishedHpCount != null ||
    pripojena?.totalHpCount != null ||
    babovka?.finishedHpCount != null ||
    babovka?.totalHpCount != null;
  if (!hasHp) return null;
  return {
    numerator:
      Math.max(0, pripojena?.finishedHpCount ?? 0) + Math.max(0, babovka?.finishedHpCount ?? 0),
    denominator:
      Math.max(0, pripojena?.totalHpCount ?? 0) + Math.max(0, babovka?.totalHpCount ?? 0),
    unit: 'hp',
  };
}

function managerSpojkovisteMetrics(
  metrics: ManagerDashboardMetrics | null | undefined,
): {
  finishedCount: number;
  totalCount: number;
  finishedHpCount?: number;
  totalHpCount?: number;
} | undefined {
  return metrics?.spojkoviste ?? metrics?.matrixSpojkoviste;
}

function emptyLengthSplit(): { excavatedLengthM: number; plannedLengthM: number; remainingUnexcavatedGeomM: number } {
  return { excavatedLengthM: 0, plannedLengthM: 0, remainingUnexcavatedGeomM: 0 };
}

function routeSplitPair(
  metrics: ManagerDashboardMetrics,
  key: 'r100' | 'met' | 'ur',
): KpiPairValue {
  const split = metrics.routes?.[key] ?? emptyLengthSplit();
  return {
    numerator: Math.max(0, split.excavatedLengthM ?? 0),
    denominator: Math.max(0, split.plannedLengthM ?? 0),
    unit: 'm',
  };
}

/** Čitatel + jmenovatel KPI karty (vykopané/plánované m, hotovo/celkem ks). */
export function kpiPairValue(id: ManagerKpiValueId, metrics: ManagerDashboardMetrics): KpiPairValue | null {
  switch (id) {
    case 'routesR100':
      return routeSplitPair(metrics, 'r100');
    case 'routesMet':
      return routeSplitPair(metrics, 'met');
    case 'routesUr':
      return routeSplitPair(metrics, 'ur');
    case 'routes':
      return {
        numerator: Math.max(0, metrics.routes?.excavatedLengthM ?? 0),
        denominator: Math.max(0, metrics.routes?.plannedLengthM ?? 0),
        unit: 'm',
      };
    case 'zafukBudovy':
      return sumZafukBudovyKsPair(metrics);
    case 'objekt':
      return {
        numerator: Math.max(0, metrics.objekt?.drilledCount ?? 0),
        denominator: Math.max(0, metrics.objekt?.totalCount ?? 0),
        unit: 'ks',
      };
    case 'rozvadec':
      return {
        numerator: Math.max(0, metrics.rozvadec?.provareneCount ?? 0),
        denominator: Math.max(0, metrics.rozvadec?.totalCount ?? 0),
        unit: 'ks',
      };
    case 'rozvadecPostaveny':
      return {
        numerator: Math.max(0, metrics.rozvadec?.postavenyCount ?? 0),
        denominator: Math.max(0, metrics.rozvadec?.totalCount ?? 0),
        unit: 'ks',
      };
    case 'rozvadecZafouknuto':
      return {
        numerator: Math.max(0, metrics.rozvadec?.zafouknutoCount ?? 0),
        denominator: Math.max(0, metrics.rozvadec?.totalCount ?? 0),
        unit: 'ks',
      };
    case 'vr':
      return {
        numerator: Math.max(0, metrics.vr?.vrBranchCount ?? 0),
        denominator: Math.max(0, metrics.vr?.totalObjektCount ?? 0),
        unit: 'ks',
      };
    case 'babovka':
      return {
        numerator: Math.max(0, metrics.babovka?.builtCount ?? 0),
        denominator: Math.max(0, metrics.babovka?.totalCount ?? 0),
        unit: 'ks',
      };
    case 'zafouknuto':
      return {
        numerator: Math.max(0, metrics.zafouknuto?.finishedCount ?? 0),
        denominator: Math.max(0, metrics.zafouknuto?.totalCount ?? 0),
        unit: 'ks',
      };
    case 'zafoukBabovka':
      return {
        numerator: Math.max(0, metrics.zafoukBabovka?.finishedCount ?? 0),
        denominator: Math.max(0, metrics.zafoukBabovka?.totalCount ?? 0),
        unit: 'ks',
      };
    case 'zafoukPripojenaBudova':
      return {
        numerator: Math.max(0, metrics.zafoukPripojenaBudova?.finishedCount ?? 0),
        denominator: Math.max(0, metrics.zafoukPripojenaBudova?.totalCount ?? 0),
        unit: 'ks',
      };
    case 'zafukOkDelka':
      return {
        numerator: Math.max(0, metrics.zafukOkDelka?.completedLengthM ?? 0),
        denominator: Math.max(0, metrics.zafukOkDelka?.totalLengthM ?? 0),
        unit: 'm',
      };
    case 'zafukSvazekDelka':
      return {
        numerator: Math.max(0, metrics.zafukSvazekDelka?.completedLengthM ?? 0),
        denominator: Math.max(0, metrics.zafukSvazekDelka?.totalLengthM ?? 0),
        unit: 'm',
      };
    case 'hpPocet':
      return {
        numerator: Math.max(
          0,
          metrics.hp?.weldedHpCount ?? metrics.hp?.connectedHpCount ?? 0,
        ),
        denominator: Math.max(0, metrics.hp?.totalHpCount ?? 0),
        unit: 'hp',
      };
    case 'akvizicePocet':
      return {
        numerator: Math.max(0, metrics.akvizice?.closedCount ?? 0),
        denominator: Math.max(0, metrics.akvizice?.totalCount ?? 0),
        unit: 'ks',
      };
    case 'matrixSpoj':
      return {
        numerator: Math.max(0, managerSpojkovisteMetrics(metrics)?.finishedCount ?? 0),
        denominator: Math.max(0, managerSpojkovisteMetrics(metrics)?.totalCount ?? 0),
        unit: 'ks',
      };
    case 'zruseneBudovy':
      return {
        numerator: Math.max(0, metrics.zruseneBudovy?.cancelledCount ?? 0),
        denominator: Math.max(0, metrics.zruseneBudovy?.totalCount ?? 0),
        unit: 'ks',
      };
    case 'digitalniCesty':
      return {
        numerator: Math.max(0, metrics.digitalniCesty?.pathCount ?? 0),
        denominator: Math.max(0, metrics.digitalniCesty?.msoCount ?? 0),
        unit: 'ks',
      };
    case 'svarObjekt':
      return {
        numerator: Math.max(0, metrics.svarObjekt?.finishedCount ?? 0),
        denominator: Math.max(0, metrics.svarObjekt?.totalCount ?? 0),
        unit: 'ks',
      };
    case 'hotoveBudovy':
      return {
        numerator: Math.max(0, metrics.hotoveBudovy?.finishedCount ?? 0),
        denominator: Math.max(0, metrics.hotoveBudovy?.totalCount ?? 0),
        unit: 'ks',
      };
    default:
      return null;
  }
}

/** Hlavní čitatel KPI karty (vykopané m / počet ks). */
export function kpiPrimaryValue(id: ManagerKpiValueId, metrics: ManagerDashboardMetrics): KpiPrimaryValue | null {
  const pair = kpiPairValue(id, metrics);
  if (!pair) return null;
  return { amount: pair.numerator, unit: pair.unit };
}

/**
 * Sekundární HP zlomek pro building dual karty (stejný scope jako ks).
 * `null` když karta není dual, nebo API payload HP ještě nemá.
 */
function readBuildingHpPair(
  block: { finishedHpCount?: number; totalHpCount?: number } | null | undefined,
): KpiPairValue | null {
  if (block?.finishedHpCount == null && block?.totalHpCount == null) return null;
  return {
    numerator: Math.max(0, block?.finishedHpCount ?? 0),
    denominator: Math.max(0, block?.totalHpCount ?? 0),
    unit: 'hp',
  };
}

export function kpiBuildingHpPairValue(
  id: ManagerKpiValueId,
  metrics: ManagerDashboardMetrics,
): KpiPairValue | null {
  if (id === 'zafukBudovy') return sumZafukBudovyHpPair(metrics);
  if (!isManagerKpiBuildingDualCardId(id)) return null;
  switch (id) {
    case 'zafoukBabovka':
      return readBuildingHpPair(metrics.zafoukBabovka);
    case 'zafoukPripojenaBudova':
      return readBuildingHpPair(metrics.zafoukPripojenaBudova);
    case 'svarObjekt':
      return readBuildingHpPair(metrics.svarObjekt);
    case 'hotoveBudovy':
      return readBuildingHpPair(metrics.hotoveBudovy);
    case 'objekt':
      return readBuildingHpPair(metrics.objekt);
    case 'vr':
      return readBuildingHpPair(metrics.vr);
    case 'matrixSpoj': {
      const block = managerSpojkovisteMetrics(metrics);
      return readBuildingHpPair(
        block
          ? {
              finishedHpCount: block.finishedHpCount,
              totalHpCount: block.totalHpCount,
            }
          : null,
      );
    }
    case 'digitalniCesty':
      return readBuildingHpPair(metrics.digitalniCesty);
    case 'akvizicePocet':
      return readBuildingHpPair(metrics.akvizice);
    case 'babovka':
      return readBuildingHpPair(metrics.babovka);
    case 'zruseneBudovy':
      return readBuildingHpPair(metrics.zruseneBudovy);
    default:
      return null;
  }
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_WEEK_SNAPSHOT_DISTANCE_MS = 10 * 24 * 60 * 60 * 1000;

/** Začátek aktuálního UTC bucketu (shodně s backend `date_trunc` / ISO pondělí). */
function truncateUtcBucketStartMs(now: Date, bucket: ManagerDashboardTimeseriesResult['bucket']): number {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const day = now.getUTCDate();
  if (bucket === 'day') {
    return Date.UTC(y, m, day);
  }
  if (bucket === 'month') {
    return Date.UTC(y, m, 1);
  }
  const wd = now.getUTCDay();
  const delta = (wd + 6) % 7;
  return Date.UTC(y, m, day - delta);
}

/**
 * Snapshot KPI nejbližší stavu před ~7 dny (z timeseries grafu).
 * Vylučuje aktuální otevřený bucket — ten má metricsSnapshot ≈ teď, ne „minulý týden“.
 */
export function findPreviousWeekMetricsSnapshot(
  timeseries: ManagerDashboardTimeseriesResult | null | undefined,
): ManagerDashboardMetrics | null {
  if (!timeseries?.points?.length) return null;
  const now = new Date();
  const targetMs = now.getTime() - WEEK_MS;
  const currentBucketStartMs = truncateUtcBucketStartMs(now, timeseries.bucket);
  let best: { dist: number; metrics: ManagerDashboardMetrics } | null = null;
  for (const point of timeseries.points) {
    const t = new Date(point.bucket).getTime();
    if (Number.isNaN(t)) continue;
    // Aktuální bucket (a budoucí) ≠ minulý týden.
    if (t >= currentBucketStartMs) continue;
    const dist = Math.abs(t - targetMs);
    if (dist > MAX_WEEK_SNAPSHOT_DISTANCE_MS) continue;
    if (!best || dist < best.dist) best = { dist, metrics: point.metricsSnapshot };
  }
  return best?.metrics ?? null;
}

function attachPriorWeek(
  card: Omit<ManagerKpiSnapshotCardView, 'priorWeek' | 'secondaryPair'> & { id: ManagerKpiCardId },
  current: ManagerDashboardMetrics | null | undefined,
  previousWeek: ManagerDashboardMetrics | null,
  secondaryPair: ManagerKpiSecondaryPairView | null,
  presentationMode: ManagerKpiPresentationMode,
): ManagerKpiSnapshotCardView {
  if (presentationMode === 'userPeriodWork') {
    return { ...card, priorWeek: null, secondaryPair: null };
  }
  const currentPair = current ? kpiPairValue(card.id, current) : null;
  const priorPair = current && previousWeek ? kpiPairValue(card.id, previousWeek) : null;
  if (!currentPair || !priorPair) {
    return { ...card, priorWeek: null, secondaryPair };
  }
  return {
    ...card,
    priorWeek:
      card.valueMode === 'absolute'
        ? formatPriorWeekAbsolute(currentPair, priorPair)
        : formatPriorWeekPair(currentPair, priorPair),
    secondaryPair,
  };
}

function buildSecondaryHpPairView(
  cardId: ManagerKpiCardId,
  metrics: ManagerDashboardMetrics | null | undefined,
  previousWeek: ManagerDashboardMetrics | null,
): ManagerKpiSecondaryPairView | null {
  if (!metrics || !isManagerKpiBuildingDualCardId(cardId)) return null;
  const hpPair = kpiBuildingHpPairValue(cardId, metrics);
  if (!hpPair) return null;
  const labels = formatPairLabels(hpPair);
  const priorHp =
    previousWeek != null ? kpiBuildingHpPairValue(cardId, previousWeek) : null;
  return {
    numeratorLabel: labels.numeratorLabel,
    denominatorLabel: labels.denominatorLabel,
    unitSuffix: labels.unitSuffix,
    valueLabel: labels.valueLabel,
    numeratorValue: labels.numeratorValue,
    denominatorValue: labels.denominatorValue,
    priorWeek: priorHp ? formatPriorWeekPair(hpPair, priorHp) : null,
  };
}

const EMPTY_PAIR_LABELS = {
  valueMode: 'fraction' as const,
  numeratorLabel: '—',
  denominatorLabel: '—',
  unitSuffix: '',
  valueLabel: '— / —',
  numeratorValue: null as number | null,
  denominatorValue: null as number | null,
};

const EMPTY_ABSOLUTE_LABELS = {
  valueMode: 'absolute' as const,
  numeratorLabel: '—',
  denominatorLabel: '',
  unitSuffix: '',
  valueLabel: '—',
  numeratorValue: null as number | null,
  denominatorValue: null as number | null,
};

function buildCardView(
  cardId: ManagerKpiCardId,
  metrics: ManagerDashboardMetrics | null | undefined,
  previousWeek: ManagerDashboardMetrics | null,
  presentationMode: ManagerKpiPresentationMode = 'portfolio',
): ManagerKpiSnapshotCardView {
  const pair = metrics ? kpiPairValue(cardId, metrics) : null;
  const absolute =
    presentationMode === 'userPeriodWork' || isManagerKpiAbsoluteCardId(cardId);
  const labels = pair
    ? absolute
      ? formatAbsoluteLabels(pair)
      : formatPairLabels(pair)
    : absolute
      ? EMPTY_ABSOLUTE_LABELS
      : EMPTY_PAIR_LABELS;
  const secondaryPair =
    presentationMode === 'userPeriodWork'
      ? null
      : buildSecondaryHpPairView(cardId, metrics, previousWeek);
  const valueLabel =
    secondaryPair != null ? `${labels.valueLabel} · ${secondaryPair.valueLabel}` : labels.valueLabel;
  return attachPriorWeek(
    {
      id: cardId,
      label: MANAGER_KPI_CARD_LABELS[cardId],
      info: MANAGER_KPI_CARD_INFO[cardId],
      seriesColor: MANAGER_KPI_SERIES_COLORS[cardId],
      ...labels,
      valueLabel,
    },
    metrics,
    previousWeek,
    secondaryPair,
    presentationMode,
  );
}

function fiberLabelForTypOk(typOk: string, fiberCount: number | null | undefined): string {
  if (fiberCount != null && Number.isFinite(fiberCount)) {
    return `${Math.round(fiberCount)} vl.`;
  }
  if (typOk === 'neuvedeno') return 'neuvedeno';
  return typOk;
}

function buildZafukOkTypBreakdown(
  metrics: ManagerDashboardMetrics | null | undefined,
): ManagerKpiOkTypBreakdownView[] {
  const rows = metrics?.zafukOkByTypOk ?? [];
  return rows
    .filter((row) => (row.completedLengthM ?? 0) > 0 || (row.totalLengthM ?? 0) > 0)
    .map((row) => {
      const numeratorLabel = fmtMeters(Math.max(0, row.completedLengthM ?? 0));
      return {
        typOk: row.typOk,
        fiberLabel: fiberLabelForTypOk(row.typOk, row.fiberCount),
        numeratorLabel,
        unitSuffix: ' m',
        valueLabel: `${numeratorLabel} m`,
      };
    });
}

/** Stejné metriky a popisky jako legenda v `manager-kpi-chart.echarts.ts`. */
export function buildManagerKpiSnapshotRows(
  metrics: ManagerDashboardMetrics | null | undefined,
  timeseries?: ManagerDashboardTimeseriesResult | null,
  presentationMode: ManagerKpiPresentationMode = 'portfolio',
): ManagerKpiSnapshotRowView[] {
  const previousWeek =
    presentationMode === 'userPeriodWork' ? null : findPreviousWeekMetricsSnapshot(timeseries);
  return MANAGER_KPI_CATEGORY_IDS.map((id) => ({
    id,
    title: MANAGER_KPI_ROW_TITLES[id],
    cards: MANAGER_KPI_CATEGORY_CARDS[id].map((cardId) =>
      buildCardView(cardId, metrics, previousWeek, presentationMode),
    ),
  }));
}

/** Plochý seznam karet ve stejném pořadí jako řady. */
export function buildManagerKpiSnapshotCards(
  metrics: ManagerDashboardMetrics | null | undefined,
  timeseries?: ManagerDashboardTimeseriesResult | null,
  presentationMode: ManagerKpiPresentationMode = 'portfolio',
): ManagerKpiSnapshotCardView[] {
  return buildManagerKpiSnapshotRows(metrics, timeseries, presentationMode).flatMap((row) => row.cards);
}

function headlineCardForCategory(
  categoryId: ManagerKpiCategoryId,
  metrics: ManagerDashboardMetrics | null | undefined,
  previousWeek: ManagerDashboardMetrics | null,
  presentationMode: ManagerKpiPresentationMode = 'portfolio',
): ManagerKpiSnapshotCardView {
  const headlineId = MANAGER_KPI_CATEGORY_HEADLINE[categoryId];
  const formatHeadline = (pair: KpiPairValue | null) => {
    if (!pair) {
      return presentationMode === 'userPeriodWork' ? EMPTY_ABSOLUTE_LABELS : EMPTY_PAIR_LABELS;
    }
    return presentationMode === 'userPeriodWork' ? formatAbsoluteLabels(pair) : formatPairLabels(pair);
  };
  if (headlineId === 'routes') {
    const pair = metrics ? kpiPairValue('routes', metrics) : null;
    const labels = formatHeadline(pair);
    return {
      id: 'routes',
      label: 'Vykopaná délka',
      info: MANAGER_KPI_HEADLINE_INFO.routes,
      seriesColor: MANAGER_KPI_SERIES_COLORS.routesR100,
      ...labels,
      priorWeek:
        presentationMode === 'userPeriodWork' || !pair || !metrics || !previousWeek
          ? null
          : formatPriorWeekPair(pair, kpiPairValue('routes', previousWeek) ?? pair),
      secondaryPair: null,
    };
  }
  // Hub Záfuk = součet připojovaných budov + bábovek (ks · HP); detail drží split karty.
  if (headlineId === 'zafukBudovy') {
    const pair = metrics ? kpiPairValue('zafukBudovy', metrics) : null;
    const labels = formatHeadline(pair);
    const secondaryPair: ManagerKpiSecondaryPairView | null =
      presentationMode === 'userPeriodWork'
        ? null
        : (() => {
            const hpPair = metrics ? kpiBuildingHpPairValue('zafukBudovy', metrics) : null;
            if (!hpPair) return null;
            const hpLabels = formatPairLabels(hpPair);
            const priorHp =
              previousWeek != null ? kpiBuildingHpPairValue('zafukBudovy', previousWeek) : null;
            return {
              numeratorLabel: hpLabels.numeratorLabel,
              denominatorLabel: hpLabels.denominatorLabel,
              unitSuffix: hpLabels.unitSuffix,
              valueLabel: hpLabels.valueLabel,
              numeratorValue: hpLabels.numeratorValue,
              denominatorValue: hpLabels.denominatorValue,
              priorWeek: priorHp ? formatPriorWeekPair(hpPair, priorHp) : null,
            };
          })();
    const valueLabel =
      secondaryPair != null ? `${labels.valueLabel} · ${secondaryPair.valueLabel}` : labels.valueLabel;
    return {
      id: 'zafukBudovy',
      label: 'Záfuk budov a bábovek',
      info: MANAGER_KPI_HEADLINE_INFO.zafukBudovy,
      seriesColor: MANAGER_KPI_SERIES_COLORS.zafukBudovy,
      ...labels,
      valueLabel,
      priorWeek:
        presentationMode === 'userPeriodWork' || !pair || !metrics || !previousWeek
          ? null
          : formatPriorWeekPair(pair, kpiPairValue('zafukBudovy', previousWeek) ?? pair),
      secondaryPair,
    };
  }
  return buildCardView(headlineId, metrics, previousWeek, presentationMode);
}

/** Hub dlaždice pro board pohled (Přehled). */
export function buildManagerKpiCategoryTiles(
  metrics: ManagerDashboardMetrics | null | undefined,
  timeseries?: ManagerDashboardTimeseriesResult | null,
  presentationMode: ManagerKpiPresentationMode = 'portfolio',
): ManagerKpiCategoryTileView[] {
  const previousWeek =
    presentationMode === 'userPeriodWork' ? null : findPreviousWeekMetricsSnapshot(timeseries);
  return MANAGER_KPI_CATEGORY_IDS.map((id) => {
    const headline = headlineCardForCategory(id, metrics, previousWeek, presentationMode);
    return {
      id,
      title: MANAGER_KPI_CATEGORY_LABELS[id],
      summary: MANAGER_KPI_CATEGORY_SUMMARIES[id],
      info: MANAGER_KPI_CATEGORY_INFO[id],
      seriesColor: headline.seriesColor,
      headline,
      drillable: isManagerKpiCategoryDrillable(id),
    };
  });
}

/** Detail jedné kategorie (karty + volitelný rozpad OK). */
export function buildManagerKpiCategoryDetail(
  categoryId: ManagerKpiCategoryId,
  metrics: ManagerDashboardMetrics | null | undefined,
  timeseries?: ManagerDashboardTimeseriesResult | null,
  presentationMode: ManagerKpiPresentationMode = 'portfolio',
): {
  id: ManagerKpiCategoryId;
  title: string;
  summary: string;
  cards: ManagerKpiSnapshotCardView[];
  okByTypOk: ManagerKpiOkTypBreakdownView[];
} {
  const previousWeek =
    presentationMode === 'userPeriodWork' ? null : findPreviousWeekMetricsSnapshot(timeseries);
  const okByTypOk =
    categoryId === 'zafuk' && presentationMode !== 'userPeriodWork'
      ? buildZafukOkTypBreakdown(metrics)
      : [];

  return {
    id: categoryId,
    title: MANAGER_KPI_CATEGORY_LABELS[categoryId],
    summary: MANAGER_KPI_CATEGORY_SUMMARIES[categoryId],
    cards: managerKpiCategoryDetailCards(categoryId).map((cardId) =>
      buildCardView(cardId, metrics, previousWeek, presentationMode),
    ),
    okByTypOk,
  };
}
